import React from 'react';
import { FileText, ShoppingBag, Video, MessageSquare, Clock, Download, Sparkles, BookOpen, ChevronRight, User as UserIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
import { User } from '../../types';
interface OverviewTabProps {
  theme: ZodiacTheme;
  user: User | null;
  setActiveTab: (tab: string) => void;
}
const kpiItems = [{
  id: 'orders',
  label: 'Заказы',
  value: 2,
  Icon: ShoppingBag
}, {
  id: 'reports',
  label: 'Отчёты',
  value: 4,
  Icon: FileText
}, {
  id: 'consultations',
  label: 'Сессии',
  value: 1,
  Icon: Video
}, {
  id: 'chat',
  label: 'Сообщения',
  value: 3,
  Icon: MessageSquare,
  alert: true
}] as const;
const reports = [{
  title: 'Натальная карта',
  date: '12 Окт 2024',
  pending: false
}, {
  title: 'Соляр 2024',
  date: '05 Окт 2024',
  pending: false
}, {
  title: 'Совместимость',
  date: 'В процессе',
  pending: true
}];
const messages = [{
  name: 'Елена Смирнова',
  msg: 'Здравствуйте! Я посмотрела вашу карту...',
  time: '10:45',
  unread: 2
}, {
  name: 'Служба поддержки',
  msg: 'Ваш заказ успешно оплачен и передан...',
  time: 'Вчера',
  unread: 0
}, {
  name: 'Дмитрий Волков',
  msg: 'Спасибо за консультацию!',
  time: '12 Окт',
  unread: 0
}];
const purchases = [{
  title: 'Основы астрологии',
  type: 'Видеокурс',
  Icon: Video
}, {
  title: 'Лунные узлы и карма',
  type: 'PDF Гайд',
  Icon: BookOpen
}];
export function OverviewTab({
  theme,
  user,
  setActiveTab
}: OverviewTabProps) {
  const {
    showToast
  } = useToast();
  const SectionHeader = ({
    title,
    tab,
    linkText = 'Все'
  }: {
    title: string;
    tab: string;
    linkText?: string;
  }) => <div className="flex justify-between items-center" data-id="element-18664">
      <h3 className="font-bold text-slate-900" data-id="element-18665">{title}</h3>
      <button onClick={() => setActiveTab(tab)} className={`text-sm font-medium ${theme.text} hover:underline flex items-center gap-0.5`} data-id="element-18666">
        {linkText} <ChevronRight className="w-4 h-4" data-id="element-18667" />
      </button>
    </div>;
  return <div className="space-y-6 pb-8" data-id="element-18668">
      {/* ── 1. Баннер ── */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-sm relative overflow-hidden" data-id="element-18669">
        <div className="absolute top-0 right-0 -mt-6 -mr-6 text-[120px] opacity-5 pointer-events-none leading-none select-none" data-id="element-18670">
          {theme.icon}
        </div>
        <div className="relative z-10" data-id="element-18671">
          <span className={`inline-block bg-slate-100 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-3 ${theme.text}`} data-id="element-18672">
            {theme.name} • {theme.element}
          </span>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1.5" data-id="element-18673">
            Добро пожаловать, {user?.name?.split(' ')[0] || 'Гость'}!
          </h2>
          <p className="text-slate-500 text-sm mb-4 max-w-lg leading-relaxed" data-id="element-18674">
            Сегодня Луна в гармоничном аспекте с вашим Солнцем — отличный день
            для новых начинаний.
          </p>
          <div className="flex gap-2.5" data-id="element-18675">
            <Button size="sm" onClick={() => showToast('Переход к выбору услуг')} data-id="element-18676">
              Заказать услугу
            </Button>
            <Button variant="outline" size="sm" onClick={() => setActiveTab('reports')} data-id="element-18677">
              Мои отчёты
            </Button>
          </div>
        </div>
      </div>

      {/* ── 2. KPI — единая полоса ── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-id="element-18678">
        <div className="flex overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]" data-id="element-18679">
          {kpiItems.map((kpi, idx) => <button key={kpi.id} onClick={() => setActiveTab(kpi.id)} className={`flex-1 min-w-[120px] flex items-center gap-2.5 px-4 py-3 hover:bg-slate-50 transition-colors relative ${idx > 0 ? 'border-l border-slate-100' : ''}`} data-id="element-18680">
              {kpi.alert && <span className="absolute top-2 right-2 w-2 h-2 bg-rose-500 rounded-full" data-id="element-18681" />}
              <div className={`w-8 h-8 rounded-lg ${kpi.alert ? 'bg-rose-50 text-rose-600' : `${theme.bg} ${theme.text}`} flex items-center justify-center shrink-0`} data-id="element-18682">
                <kpi.Icon className="w-4 h-4" data-id="element-18683" />
              </div>
              <div className="text-left" data-id="element-18684">
                <div className="text-lg font-bold text-slate-900 leading-none" data-id="element-18685">
                  {kpi.value}
                </div>
                <div className="text-[11px] text-slate-500 font-medium mt-0.5" data-id="element-18686">
                  {kpi.label}
                </div>
              </div>
            </button>)}
        </div>
      </div>

      {/* ── 3. Ближайшая сессия ── */}
      <section className="space-y-2.5" data-id="element-18687">
        <SectionHeader title="Ближайшая сессия" tab="consultations" data-id="element-18688" />
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm relative overflow-hidden" data-id="element-18689">
          <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500 rounded-l-xl" data-id="element-18690" />
          <div className="pl-3" data-id="element-18691">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3" data-id="element-18692">
              <div className="flex-1 min-w-0" data-id="element-18693">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap" data-id="element-18694">
                  <Badge variant="pro" size="sm" className="bg-indigo-50 text-indigo-700" data-id="element-18695">
                    Ожидает
                  </Badge>
                  <span className="text-xs text-slate-500 flex items-center gap-1" data-id="element-18696">
                    <Video className="w-3.5 h-3.5" data-id="element-18697" /> Видеозвонок
                  </span>
                </div>
                <h4 className="font-bold text-slate-900 mb-1" data-id="element-18698">
                  Синастрия (Совместимость)
                </h4>
                <div className="flex items-center gap-2 text-sm text-slate-600 mb-2" data-id="element-18699">
                  <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-[9px] shrink-0" data-id="element-18700">
                    ЕС
                  </div>
                  <span data-id="element-18701">Елена Смирнова</span>
                </div>
                <div className="inline-flex items-center bg-amber-50 text-amber-800 text-xs font-medium px-2.5 py-1 rounded-md border border-amber-100" data-id="element-18702">
                  <Clock className="w-3.5 h-3.5 mr-1.5 text-amber-500" data-id="element-18703" />
                  Завтра, 15 Окт в 14:00
                </div>
              </div>
              <div className="flex gap-2 w-full sm:w-auto shrink-0 pt-1" data-id="element-18704">
                <Button variant="outline" size="sm" className="flex-1 sm:flex-none" onClick={() => setActiveTab('chat')} data-id="element-18705">
                  <MessageSquare className="w-4 h-4 mr-1.5" data-id="element-18706" /> Чат
                </Button>
                <Button variant="primary" size="sm" className="flex-1 sm:flex-none bg-indigo-600 hover:bg-indigo-700" onClick={() => showToast('Ожидайте ссылку на звонок за 10 минут до начала')} data-id="element-18707">
                  Войти в звонок
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. Последние отчёты ── */}
      <section className="space-y-2.5" data-id="element-18708">
        <SectionHeader title="Последние отчёты" tab="reports" data-id="element-18709" />
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm divide-y divide-slate-100" data-id="element-18710">
          {reports.map((r, i) => <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors group cursor-pointer" onClick={() => setActiveTab('reports')} data-id="element-18711">
              <div className="flex items-center gap-3 min-w-0" data-id="element-18712">
                <div className={`w-9 h-9 rounded-lg ${r.pending ? 'bg-slate-100 text-slate-400' : `${theme.bg} ${theme.text}`} flex items-center justify-center shrink-0`} data-id="element-18713">
                  <FileText className="w-4 h-4" data-id="element-18714" />
                </div>
                <div className="min-w-0" data-id="element-18715">
                  <div className="font-semibold text-sm text-slate-900 truncate" data-id="element-18716">
                    {r.title}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5" data-id="element-18717">
                    {r.date}
                    {r.pending && <Badge variant="warning" size="sm" className="text-[9px] py-0 px-1 h-3.5 leading-none" data-id="element-18718">
                        В работе
                      </Badge>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 ml-2" data-id="element-18719">
                {!r.pending && <button className="p-1.5 text-slate-400 hover:text-slate-700 transition-colors hidden sm:block" title="Скачать PDF" onClick={e => {
              e.stopPropagation();
              showToast('Скачивание отчёта...');
            }} data-id="element-18720">
                    <Download className="w-4 h-4" data-id="element-18721" />
                  </button>}
                <Button variant={r.pending ? 'outline' : 'secondary'} size="sm" className="text-xs px-2.5 h-7" onClick={e => {
              e.stopPropagation();
              setActiveTab('reports');
            }} data-id="element-18722">
                  Открыть
                </Button>
              </div>
            </div>)}
        </div>
      </section>

      {/* ── 5. Сообщения ── */}
      <section className="space-y-2.5" data-id="element-18723">
        <SectionHeader title="Сообщения" tab="chat" data-id="element-18724" />
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm divide-y divide-slate-100" data-id="element-18725">
          {messages.map((m, i) => <div key={i} className={`flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer ${m.unread > 0 ? 'bg-indigo-50/30' : ''}`} onClick={() => setActiveTab('chat')} data-id="element-18726">
              <div className="relative shrink-0" data-id="element-18727">
                <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-xs" data-id="element-18728">
                  {m.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                </div>
                {m.unread > 0 && <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-rose-500 rounded-full border-2 border-white flex items-center justify-center text-[8px] font-bold text-white" data-id="element-18729">
                    {m.unread}
                  </span>}
              </div>
              <div className="flex-1 min-w-0" data-id="element-18730">
                <div className="flex justify-between items-baseline gap-2" data-id="element-18731">
                  <span className={`text-sm truncate ${m.unread > 0 ? 'font-bold text-slate-900' : 'font-medium text-slate-700'}`} data-id="element-18732">
                    {m.name}
                  </span>
                  <span className={`text-[10px] shrink-0 ${m.unread > 0 ? 'text-indigo-600 font-bold' : 'text-slate-400'}`} data-id="element-18733">
                    {m.time}
                  </span>
                </div>
                <div className={`text-xs truncate mt-0.5 ${m.unread > 0 ? 'text-slate-800' : 'text-slate-500'}`} data-id="element-18734">
                  {m.msg}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 shrink-0 hidden sm:block" data-id="element-18735" />
            </div>)}
        </div>
      </section>

      {/* ── 6. Астропрофиль ── */}
      <section className="space-y-2.5" data-id="element-18736">
        <SectionHeader title="Мой астропрофиль" tab="profile" linkText="Все профили" data-id="element-18737" />
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-id="element-18738">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-18739">
            <div className="flex items-center gap-3" data-id="element-18740">
              <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${theme.gradient} flex items-center justify-center text-white text-lg shadow-sm shrink-0`} data-id="element-18741">
                {theme.icon}
              </div>
              <div data-id="element-18742">
                <div className="flex items-center gap-1.5" data-id="element-18743">
                  <span className="font-bold text-slate-900 text-sm" data-id="element-18744">
                    {user?.name || 'Мой профиль'}
                  </span>
                  <Badge variant="neutral" size="sm" className="text-[9px] py-0 h-3.5 px-1" data-id="element-18745">
                    Основной
                  </Badge>
                </div>
                <div className="text-xs text-slate-500 mt-0.5" data-id="element-18746">
                  {theme.name} • 12 Апр 1990, 14:30 • Москва
                </div>
              </div>
            </div>
            <div className="flex gap-2 w-full sm:w-auto shrink-0" data-id="element-18747">
              <Button variant="secondary" size="sm" className="flex-1 sm:flex-none text-xs" onClick={() => showToast('Профиль выбран для нового заказа')} data-id="element-18748">
                Использовать
              </Button>
              <Button variant="outline" size="sm" className="px-2.5" onClick={() => setActiveTab('profile')} title="Редактировать" data-id="element-18749">
                <UserIcon className="w-4 h-4" data-id="element-18750" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── 7. Покупки ── */}
      <section className="space-y-2.5" data-id="element-18751">
        <SectionHeader title="Недавние покупки" tab="purchases" data-id="element-18752" />
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm divide-y divide-slate-100" data-id="element-18753">
          {purchases.map((p, i) => <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors" data-id="element-18754">
              <div className="flex items-center gap-3 min-w-0" data-id="element-18755">
                <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0" data-id="element-18756">
                  <p.Icon className="w-4 h-4" data-id="element-18757" />
                </div>
                <div className="min-w-0" data-id="element-18758">
                  <div className="font-semibold text-sm text-slate-900 truncate" data-id="element-18759">
                    {p.title}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-18760">{p.type}</div>
                </div>
              </div>
              <Button variant="ghost" size="sm" className={`text-xs font-medium ${theme.text} hover:bg-indigo-50 shrink-0 ml-2`} onClick={() => showToast(`Открытие: ${p.title}`)} data-id="element-18761">
                Открыть
              </Button>
            </div>)}
        </div>
      </section>
    </div>;
}